<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LlmCredentialCard from './LlmCredentialCard.vue';
import LlmCredentialDialog from './LlmCredentialDialog.vue';
import { isProviderDefault } from '../llmCredentials';

const { t } = useI18n();
const store = useLlmCredentialStore();
const { records } = storeToRefs(store);

const formRef = ref(null);
const deleteDialogRef = ref(null);
const pendingDelete = ref(null);

const openForm = credential => formRef.value.open(credential);

const askDelete = credential => {
  pendingDelete.value = credential;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  try {
    await store.delete(pendingDelete.value.id);
    useAlert(t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.API.DELETED'));
    deleteDialogRef.value.close();
    // Which features each LLM serves depends on all of them, so the list is read again whole.
    store.fetch();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.API.ERROR')
    );
  }
};

onMounted(() => store.fetch());

defineExpose({ openForm });
</script>

<template>
  <div class="grid gap-3">
    <LlmCredentialCard
      v-for="credential in records"
      :key="credential.id"
      :credential="credential"
      :is-default="isProviderDefault(records, credential)"
      @edit="openForm"
      @delete="askDelete"
    />

    <div class="flex justify-start">
      <NextButton
        type="button"
        faded
        slate
        icon="i-lucide-plus"
        :label="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.ADD')"
        @click="openForm()"
      />
    </div>

    <LlmCredentialDialog ref="formRef" @saved="store.fetch()" />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="
        t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DELETE_DIALOG.TITLE', {
          name: pendingDelete?.name,
        })
      "
      :description="
        pendingDelete?.features.length
          ? t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DELETE_DIALOG.IN_USE')
          : t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DELETE_DIALOG.UNUSED')
      "
      :confirm-button-label="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DELETE')"
      @confirm="confirmDelete"
    />
  </div>
</template>
